/**
 * EvidenceChatSkeleton Component
 * EVI-487: Custom skeleton loader for Evidence chat page
 *
 * Provides two skeleton variants:
 * 1. Home page skeleton: Logo + input area (for /scribe/chat)
 * 2. Chat content skeleton: Timeline with messages, search, key points (for /scribe/chat/[id])
 */

import { IS_INSET_DESIGN_ENABLED } from '@/app-shell/flags/shell-flags';
import { Skeleton } from '@heidi/design-system/skeleton';
import { useIsMobile } from '@heidi/utils';
import { cn } from '@/lib/cn';

/** Skeleton for the logo animation in the empty state */
const LogoSkeleton = () => (
  <div className="flex flex-col items-center gap-4">
    {/* Logo placeholder */}
    <Skeleton className="size-10 rounded-full" />
    {/* Greeting text placeholder */}
    <Skeleton className="h-8 w-80" />
  </div>
);

/** Skeleton for the chat input area */
const ChatInputSkeleton = ({ isMobile }: { isMobile: boolean }) => (
  <div
    className={cn(
      'w-full rounded-xl border border-border bg-surface shadow-sm',
      isMobile ? 'p-3' : 'p-4'
    )}
  >
    <div className="flex w-full flex-col">
      {/* Input area placeholder */}
      <div className="mb-4 flex-1">
        <Skeleton className="h-5 w-3/4" />
      </div>

      {/* Bottom bar: Controls */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {/* Source badges placeholder */}
          <Skeleton className="h-7 w-24 rounded-full" />
          <Skeleton className="h-7 w-32 rounded-full" />
        </div>

        {/* Submit button placeholder */}
        <Skeleton className="size-8 rounded-lg" />
      </div>
    </div>
  </div>
);

/** Skeleton for a user message bubble */
const UserMessageSkeleton = () => (
  <div className="flex w-full justify-end">
    <div className="max-w-md rounded-lg bg-background-secondary px-3 py-1.5">
      <Skeleton className="h-4 w-64" />
    </div>
  </div>
);

/** Skeleton for search/thinking blocks */
const SearchBlocksSkeleton = () => (
  <div className="flex w-full flex-col gap-0 overflow-hidden rounded-lg border border-border">
    {/* First search item */}
    <div className="flex items-center gap-3 border-b border-border px-4 py-3">
      <Skeleton className="size-4 shrink-0 rounded" />
      <Skeleton className="h-4 flex-1" />
      <Skeleton className="h-4 w-20" />
    </div>
    {/* Second search item */}
    <div className="flex items-center gap-3 px-4 py-3">
      <Skeleton className="size-4 shrink-0 rounded" />
      <Skeleton className="h-4 flex-1" />
      <Skeleton className="h-4 w-20" />
    </div>
  </div>
);

/** Skeleton for key points section */
const KeyPointsSkeleton = () => (
  <div className="rounded-xl border border-border bg-surface p-5">
    {/* Header */}
    <div className="mb-4 flex items-center gap-2">
      <Skeleton className="size-5 rounded" />
      <Skeleton className="h-5 w-24" />
    </div>
    {/* Bullet points */}
    <div className="space-y-3 pl-1">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex items-start gap-2">
          <Skeleton className="mt-1.5 size-1.5 shrink-0 rounded-full" />
          <Skeleton className="h-4" style={{ width: `${75 - i * 10}%` }} />
        </div>
      ))}
    </div>
  </div>
);

/** Skeleton for content sections below key points */
const ContentSkeleton = () => (
  <div className="space-y-6">
    {/* Section heading */}
    <Skeleton className="h-6 w-72" />
    {/* Subheading */}
    <Skeleton className="h-5 w-56" />
    {/* Content paragraphs */}
    <div className="space-y-2">
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-11/12" />
      <Skeleton className="h-4 w-4/5" />
    </div>
  </div>
);

interface EvidenceChatSkeletonProps {
  /** Skeleton variant: 'home' for empty chat page, 'chat' for loading existing chat */
  variant?: 'home' | 'chat';
  /** @deprecated Use variant instead. Kept for backwards compatibility. */
  isSessionless?: boolean;
  className?: string;
}

/**
 * Evidence Chat Page Skeleton Loader
 *
 * Two variants:
 * - 'home': Logo + input for the empty chat homepage
 * - 'chat': Timeline with user message, search blocks, key points for loading existing chats
 */
export const EvidenceChatSkeleton = ({
  variant,
  isSessionless,
  className,
}: EvidenceChatSkeletonProps) => {
  const isMobile = useIsMobile();

  // Determine variant from props (backwards compatible)
  const effectiveVariant = variant ?? (isSessionless === false ? 'chat' : 'home');

  // ===== CHAT CONTENT SKELETON =====
  if (effectiveVariant === 'chat') {
    return (
      <div className={cn('flex size-full flex-col', IS_INSET_DESIGN_ENABLED() ? 'bg-surface-shell' : 'bg-surface-page', className)}>
        {/* Scrollable timeline area */}
        <div className="flex flex-1 flex-col items-center overflow-hidden p-4">
          <div className="w-full max-w-3xl space-y-6">
            {/* User message */}
            <UserMessageSkeleton />

            {/* Search/thinking blocks */}
            <SearchBlocksSkeleton />

            {/* Key points */}
            <KeyPointsSkeleton />

            {/* Content below */}
            <ContentSkeleton />
          </div>
        </div>
      </div>
    );
  }

  // ===== HOME PAGE SKELETON =====
  if (isMobile) {
    return (
      <div
        className={cn(
          'flex size-full flex-col overflow-hidden',
          IS_INSET_DESIGN_ENABLED() ? 'bg-surface-shell' : 'bg-surface-page',
          className
        )}
      >
        {/* Top section: Logo and greeting */}
        <div className="flex flex-1 flex-col items-center justify-center px-4">
          <LogoSkeleton />
        </div>

        {/* Bottom section: Input */}
        <div className="shrink-0 px-4 pb-4">
          <ChatInputSkeleton isMobile />
        </div>
      </div>
    );
  }

  // Desktop home layout - centered with slight upward offset to match actual page
  return (
    <div
      className={cn(
        'flex size-full flex-col items-center justify-center gap-6 overflow-hidden pb-[8vh]',
        IS_INSET_DESIGN_ENABLED() ? 'bg-surface-shell' : 'bg-surface-page',
        className
      )}
    >
      {/* Logo and greeting */}
      <div className="flex w-full max-w-3xl flex-col items-center">
        <LogoSkeleton />
      </div>

      {/* Input area */}
      <div className="w-full max-w-3xl shrink-0">
        <ChatInputSkeleton isMobile={false} />
      </div>
    </div>
  );
};
